import {
  EMAIL_DOCUMENT_KINDS,
  type EmailConnection,
  type EmailConnectionReturn,
  type EmailDocumentKind,
  type EmailProvider,
} from '@my/directory-api';
import {
  useDeleteEmailConnection,
  useOrganisationEmailConnections,
  useSendEmailConnectionTest,
  useStartEmailConnection,
  useUpdateEmailConnection,
} from '@my/directory-api/hooks';
import {
  Button,
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  TextInput,
  showToast,
} from '@my/ui';
import { Copy, Edit, Mail, Plus, Send, Star, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

export interface EmailSendingSectionProps {
  organisationId: string | null;
  /**
   * The organisation's invoicing name — the From name a mailbox uses unless
   * it is given its own (and what Stratum's sender already uses).
   */
  invoicingName: string | null;
}

/**
 * Settings → Organisation → Email sending. Connects the organisation's own
 * Microsoft 365 or Google mailbox, so documents reach customers from its
 * address rather than My Stratum's.
 *
 * <p>
 * The sign-in itself happens on Microsoft's / Google's site: Connect sends the
 * browser there, and Directory brings it back to THIS page with
 * <c>?emailConnection=…</c>, which is read once, explained, and removed.
 * </p>
 *
 * <p>
 * Only account owners and admins change anything (Directory enforces it and
 * says so in <c>canManage</c>); everyone else sees the list read-only.
 * </p>
 */
export function EmailSendingSection({ organisationId, invoicingName }: EmailSendingSectionProps) {
  const { t } = useTranslation('organisation');
  const listQuery = useOrganisationEmailConnections(organisationId);
  const startMutation = useStartEmailConnection();
  const testMutation = useSendEmailConnectionTest();
  const deleteMutation = useDeleteEmailConnection();

  const [choosing, setChoosing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<EmailConnection | null>(null);
  const [notice, setNotice] = useState<EmailConnectionReturn | null>(null);
  const [adminConsentUrl, setAdminConsentUrl] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Coming back from Microsoft / Google ────────────────────────────────
  useEffect(() => {
    const outcome = searchParams.get('emailConnection') as EmailConnectionReturn | null;
    if (!outcome) return;
    const connectionId = searchParams.get('connectionId');

    setNotice(outcome);
    // A fresh connection still needs its "Use for" and a test email.
    if (outcome === 'connected' && connectionId) setEditingId(connectionId);

    const next = new URLSearchParams(searchParams);
    next.delete('emailConnection');
    next.delete('connectionId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const data = listQuery.data;
  const canManage = data?.canManage ?? false;
  const anyAvailable = !!data && (data.microsoftAvailable || data.googleAvailable);

  /** Sends the browser to Microsoft / Google. Comes back to this exact page. */
  const connect = async (provider: EmailProvider, connectionId?: string) => {
    if (!organisationId) return;
    try {
      const { authorizationUrl } = await startMutation.mutateAsync({
        organisationId,
        provider,
        returnUrl: window.location.origin + window.location.pathname,
        connectionId: connectionId ?? null,
      });
      window.location.assign(authorizationUrl);
    } catch (err) {
      showToast({ type: 'error', message: errorText(err, t('emailSending.connectFailed')) });
    }
  };

  const getAdminConsentLink = async () => {
    if (!organisationId) return;
    try {
      const { authorizationUrl } = await startMutation.mutateAsync({
        organisationId,
        provider: 'Microsoft',
        returnUrl: window.location.origin + window.location.pathname,
        adminConsent: true,
      });
      setAdminConsentUrl(authorizationUrl);
    } catch (err) {
      showToast({ type: 'error', message: errorText(err, t('emailSending.connectFailed')) });
    }
  };

  const sendTest = async (connection: EmailConnection) => {
    try {
      await testMutation.mutateAsync({
        organisationId: connection.organisationId,
        id: connection.id,
      });
      showToast({ type: 'success', message: t('emailSending.testSent') });
    } catch (err) {
      showToast({ type: 'error', message: errorText(err, t('emailSending.testFailed')) });
    }
  };

  const confirmDelete = async () => {
    const row = pendingDelete;
    if (!row) return;
    try {
      await deleteMutation.mutateAsync({ organisationId: row.organisationId, id: row.id });
      showToast({ type: 'success', message: t('emailSending.disconnected') });
      if (editingId === row.id) setEditingId(null);
    } catch (err) {
      showToast({ type: 'error', message: errorText(err, t('emailSending.disconnectFailed')) });
    } finally {
      setPendingDelete(null);
    }
  };

  const headerAction =
    canManage && anyAvailable && !choosing ? (
      <button
        type="button"
        onClick={() => setChoosing(true)}
        disabled={!organisationId}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('emailSending.connect')}
      </button>
    ) : null;

  return (
    <FormSection title={t('emailSending.title')} columns={1} actions={headerAction}>
      {/* Two columns on wide screens: what this is on the left, the mailboxes
          on the right — the explanation is read once, the list is worked in. */}
      <div className="grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="flex flex-col justify-center gap-2 px-4 py-3 text-center text-sm text-slate-600 lg:px-8">
          <p>{t('emailSending.intro')}</p>
          <p>{t('emailSending.introPrivacy')}</p>
          {data && !canManage && (
            <p className="text-xs text-slate-500">{t('emailSending.adminsOnly')}</p>
          )}
        </div>

        <div className="min-w-0 space-y-3">
          {notice && (
            <ReturnNotice
              outcome={notice}
              onDismiss={() => {
                setNotice(null);
                setAdminConsentUrl(null);
              }}
              onGetAdminLink={canManage ? getAdminConsentLink : undefined}
              adminConsentUrl={adminConsentUrl}
              busy={startMutation.isPending}
            />
          )}

          {choosing && data && (
            <ProviderChooser
              microsoftAvailable={data.microsoftAvailable}
              googleAvailable={data.googleAvailable}
              busy={startMutation.isPending}
              onChoose={(p) => void connect(p)}
              onCancel={() => setChoosing(false)}
            />
          )}

          {listQuery.isPending && <Loading label={t('emailSending.loading')} />}
          {listQuery.isError && (
            <ErrorBanner title={t('emailSending.loadFailed')} message={listQuery.error.message} />
          )}

          {data && data.connections.length === 0 && !choosing && (
            <p className="rounded-md border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500">
              {anyAvailable ? t('emailSending.empty') : t('emailSending.notAvailable')}
            </p>
          )}

          {data && data.connections.length > 0 && (
            <ul className="space-y-2">
              {data.connections.map((c) =>
                editingId === c.id && canManage ? (
                  <ConnectionEditor
                    key={c.id}
                    connection={c}
                    invoicingName={invoicingName}
                    onClose={() => setEditingId(null)}
                    onTest={() => void sendTest(c)}
                    testing={testMutation.isPending}
                  />
                ) : (
                  <ConnectionRow
                    key={c.id}
                    connection={c}
                    canManage={canManage}
                    testing={testMutation.isPending && testMutation.variables?.id === c.id}
                    onEdit={() => setEditingId(c.id)}
                    onTest={() => void sendTest(c)}
                    onReconnect={() => void connect(c.provider, c.id)}
                    onDisconnect={() => setPendingDelete(c)}
                  />
                ),
              )}
            </ul>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('emailSending.disconnectTitle')}
        message={
          pendingDelete
            ? t('emailSending.disconnectMessage', { address: pendingDelete.sendFromAddress })
            : undefined
        }
        confirmLabel={t('emailSending.disconnect')}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </FormSection>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────

function ProviderChooser({
  microsoftAvailable,
  googleAvailable,
  busy,
  onChoose,
  onCancel,
}: {
  microsoftAvailable: boolean;
  googleAvailable: boolean;
  busy: boolean;
  onChoose: (provider: EmailProvider) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('organisation');
  const [provider, setProvider] = useState<EmailProvider | null>(
    microsoftAvailable ? 'Microsoft' : googleAvailable ? 'Google' : null,
  );

  const option = (p: EmailProvider, available: boolean, name: string, hint: string) => (
    <button
      type="button"
      disabled={!available}
      onClick={() => setProvider(p)}
      className={`flex-1 rounded-lg border p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
        provider === p
          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <ProviderBadge provider={p} />
      <p className="mt-2 text-sm font-semibold text-slate-900">{name}</p>
      <p className="mt-0.5 text-xs text-slate-600">{hint}</p>
    </button>
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">{t('emailSending.chooseProvider')}</p>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600"
          aria-label={t('emailSending.cancel')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {option('Microsoft', microsoftAvailable, 'Microsoft 365', t('emailSending.microsoftHint'))}
        {option(
          'Google',
          googleAvailable,
          'Google Workspace / Gmail',
          t('emailSending.googleHint'),
        )}
      </div>

      {provider && (
        <p className="mt-3 rounded-md bg-white px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
          {provider === 'Google'
            ? t('emailSending.googleBefore')
            : t('emailSending.microsoftBefore')}
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} type="button">
          {t('emailSending.cancel')}
        </Button>
        <Button
          size="sm"
          type="button"
          disabled={!provider || busy}
          onClick={() => provider && onChoose(provider)}
        >
          {provider === 'Google'
            ? t('emailSending.signInGoogle')
            : t('emailSending.signInMicrosoft')}
        </Button>
      </div>
    </div>
  );
}

function ConnectionRow({
  connection: c,
  canManage,
  testing,
  onEdit,
  onTest,
  onReconnect,
  onDisconnect,
}: {
  connection: EmailConnection;
  canManage: boolean;
  testing: boolean;
  onEdit: () => void;
  onTest: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
}) {
  const { t } = useTranslation('organisation');
  const sharedMailbox = c.sendFromAddress.toLowerCase() !== c.signedInAddress.toLowerCase();

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-start gap-3">
        <ProviderBadge provider={c.provider} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-slate-900">
              {c.sendFromName ? `${c.sendFromName} <${c.sendFromAddress}>` : c.sendFromAddress}
            </span>
            <StatusPill status={c.status} />
            {c.isDefault && (
              <span title={t('emailSending.defaultMailbox')} className="inline-flex">
                <Star
                  className="h-4 w-4 fill-amber-400 text-amber-400"
                  role="img"
                  aria-label={t('emailSending.defaultMailbox')}
                />
              </span>
            )}
          </div>
          {sharedMailbox && (
            <p className="mt-0.5 text-xs text-slate-500">
              {t('emailSending.signedInAs', { address: c.signedInAddress })}
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-600">
            {c.usedFor.map((k) => (
              <KindTag key={k} kind={k} />
            ))}
            {c.usedFor.length === 0 && (
              <span>
                {c.isDefault ? t('emailSending.usedForDefault') : t('emailSending.usedForNothing')}
              </span>
            )}
            {c.usedFor.length > 0 && c.isDefault && (
              <span>{t('emailSending.andEverythingElse')}</span>
            )}
          </div>
          {c.status === 'Pending' && (
            <p className="mt-1 text-xs text-amber-700">{t('emailSending.pendingHint')}</p>
          )}
          {c.lastFailureReason && c.status !== 'Active' && (
            <p className="mt-1 text-xs text-red-700">{c.lastFailureReason}</p>
          )}
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center gap-1">
            {c.status === 'NeedsReconnect' ? (
              <Button size="xs" type="button" onClick={onReconnect}>
                {t('emailSending.reconnect')}
              </Button>
            ) : (
              <Button size="xs" variant="outline" type="button" onClick={onTest} disabled={testing}>
                <Send className="mr-1 h-3 w-3" />
                {testing ? t('emailSending.testing') : t('emailSending.sendTest')}
              </Button>
            )}
            <button
              type="button"
              onClick={onEdit}
              aria-label={`${t('emailSending.edit')} ${c.sendFromAddress}`}
              title={t('emailSending.edit')}
              className="rounded-sm p-1 text-blue-600 hover:bg-blue-50"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onDisconnect}
              aria-label={`${t('emailSending.disconnect')} ${c.sendFromAddress}`}
              className="rounded-sm p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function ConnectionEditor({
  connection,
  invoicingName,
  onClose,
  onTest,
  testing,
}: {
  connection: EmailConnection;
  invoicingName: string | null;
  onClose: () => void;
  onTest: () => void;
  testing: boolean;
}) {
  const { t } = useTranslation('organisation');
  const updateMutation = useUpdateEmailConnection();
  const [address, setAddress] = useState(connection.sendFromAddress);
  // No name of its own = the invoicing name, shown so it's clear what customers see.
  const [name, setName] = useState(connection.sendFromName ?? invoicingName ?? '');
  const [kinds, setKinds] = useState<EmailDocumentKind[]>(connection.usedFor);
  const [isDefault, setIsDefault] = useState(connection.isDefault);

  const addressValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.trim());
  const addressChanged = address.trim().toLowerCase() !== connection.sendFromAddress.toLowerCase();

  const toggle = (kind: EmailDocumentKind) =>
    setKinds((current) =>
      current.includes(kind) ? current.filter((k) => k !== kind) : [...current, kind],
    );

  const save = async (thenTest: boolean) => {
    if (!addressValid) return;
    try {
      await updateMutation.mutateAsync({
        id: connection.id,
        sendFromAddress: address.trim(),
        // Left as the invoicing name (or emptied), it's stored as "none of its
        // own" — so renaming the organisation later carries through.
        sendFromName: !name.trim() || name.trim() === invoicingName?.trim() ? null : name.trim(),
        usedFor: EMAIL_DOCUMENT_KINDS.filter((k) => kinds.includes(k)),
        isDefault,
      });
      if (thenTest) onTest();
      else showToast({ type: 'success', message: t('emailSending.saved') });
      onClose();
    } catch (err) {
      showToast({ type: 'error', message: errorText(err, t('emailSending.saveFailed')) });
    }
  };

  const needsTest = connection.status !== 'Active' || addressChanged;

  return (
    <li className="rounded-lg border border-blue-300 bg-blue-50/40 p-4">
      <div className="mb-3 flex items-center gap-2">
        <ProviderBadge provider={connection.provider} />
        <p className="text-sm font-semibold text-slate-900">
          {t('emailSending.signedInAs', { address: connection.signedInAddress })}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput
          label={t('emailSending.fromAddress')}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          error={!addressValid}
          errorMessage={!addressValid ? t('emailSending.fromAddressInvalid') : undefined}
          helperText={
            connection.provider === 'Microsoft'
              ? t('emailSending.fromAddressHelpMicrosoft')
              : t('emailSending.fromAddressHelpGoogle')
          }
        />
        <TextInput
          label={t('emailSending.fromName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={invoicingName ?? t('emailSending.fromNamePlaceholder')}
          helperText={t('emailSending.fromNameHelp')}
          maxLength={128}
        />
      </div>

      <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-medium text-slate-700">{t('emailSending.useFor')}</p>
          <div className="mt-2">
            <KindToggles kinds={kinds} onToggle={toggle} />
          </div>
          <p className="mt-1.5 text-xs text-slate-500">{t('emailSending.useForHelp')}</p>
        </div>

        {/* A small inline switch, not a full-width field: it's one yes/no. */}
        <div
          className="flex shrink-0 items-center gap-2 self-start"
          title={t('emailSending.makeDefaultHelp')}
        >
          <button
            type="button"
            role="switch"
            aria-checked={isDefault}
            aria-label={t('emailSending.makeDefault')}
            onClick={() => setIsDefault((v) => !v)}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
              isDefault ? 'bg-amber-400' : 'bg-slate-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                isDefault ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
          <Star
            className={`h-4 w-4 ${isDefault ? 'fill-amber-400 text-amber-400' : 'text-slate-400'}`}
            aria-hidden
          />
          <span className="text-sm text-slate-800">{t('emailSending.makeDefault')}</span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" type="button" onClick={onClose}>
          {t('emailSending.cancel')}
        </Button>
        {needsTest ? (
          <Button
            size="sm"
            type="button"
            disabled={!addressValid || updateMutation.isPending || testing}
            onClick={() => void save(true)}
          >
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {t('emailSending.saveAndTest')}
          </Button>
        ) : (
          <Button
            size="sm"
            type="button"
            disabled={!addressValid || updateMutation.isPending}
            onClick={() => void save(false)}
          >
            {t('emailSending.save')}
          </Button>
        )}
      </div>
    </li>
  );
}

function ReturnNotice({
  outcome,
  onDismiss,
  onGetAdminLink,
  adminConsentUrl,
  busy,
}: {
  outcome: EmailConnectionReturn;
  onDismiss: () => void;
  onGetAdminLink?: () => void;
  adminConsentUrl: string | null;
  busy: boolean;
}) {
  const { t } = useTranslation('organisation');
  const tone =
    outcome === 'connected' || outcome === 'admin_consent_granted'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : outcome === 'admin_consent_required' || outcome === 'denied'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-red-200 bg-red-50 text-red-900';

  const copy = async () => {
    if (!adminConsentUrl) return;
    try {
      await navigator.clipboard.writeText(adminConsentUrl);
      showToast({ type: 'success', message: t('emailSending.linkCopied') });
    } catch {
      // Clipboard blocked: the link is on screen to copy by hand.
    }
  };

  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${tone}`}>
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{t(`emailSending.return.${outcome}.title`)}</p>
          <p className="mt-0.5 text-xs opacity-90">{t(`emailSending.return.${outcome}.body`)}</p>

          {outcome === 'admin_consent_required' && onGetAdminLink && !adminConsentUrl && (
            <Button
              size="xs"
              className="mt-2"
              type="button"
              onClick={onGetAdminLink}
              disabled={busy}
            >
              {t('emailSending.getAdminLink')}
            </Button>
          )}
          {adminConsentUrl && (
            <div className="mt-2 flex items-center gap-2">
              <input
                readOnly
                value={adminConsentUrl}
                onFocus={(e) => e.target.select()}
                className="min-w-0 flex-1 rounded border border-amber-300 bg-white px-2 py-1 text-xs text-slate-700"
              />
              <Button size="xs" variant="outline" type="button" onClick={() => void copy()}>
                <Copy className="mr-1 h-3 w-3" />
                {t('emailSending.copy')}
              </Button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('emailSending.dismiss')}
          className="opacity-60 hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * One colour per kind of document — the same toggle-chip look as the roles on
 * Trip Checkpoints, so "on" reads the same everywhere.
 */
const KIND_STYLE: Record<EmailDocumentKind, { on: string; off: string }> = {
  Invoices: {
    on: 'bg-emerald-600 text-white ring-emerald-600',
    off: 'bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50',
  },
  Quotes: {
    on: 'bg-sky-600 text-white ring-sky-600',
    off: 'bg-white text-sky-700 ring-sky-200 hover:bg-sky-50',
  },
  Pods: {
    on: 'bg-amber-500 text-white ring-amber-500',
    off: 'bg-white text-amber-700 ring-amber-200 hover:bg-amber-50',
  },
  Operations: {
    on: 'bg-violet-600 text-white ring-violet-600',
    off: 'bg-white text-violet-700 ring-violet-200 hover:bg-violet-50',
  },
};

function KindToggles({
  kinds,
  onToggle,
}: {
  kinds: EmailDocumentKind[];
  onToggle: (kind: EmailDocumentKind) => void;
}) {
  const { t } = useTranslation('organisation');
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {EMAIL_DOCUMENT_KINDS.map((kind) => {
        const on = kinds.includes(kind);
        return (
          <button
            key={kind}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(kind)}
            title={t(`emailSending.kindHints.${kind}`)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 transition-colors ${
              on ? KIND_STYLE[kind].on : KIND_STYLE[kind].off
            }`}
          >
            {t(`emailSending.kinds.${kind}`)}
          </button>
        );
      })}
    </span>
  );
}

function KindTag({ kind }: { kind: EmailDocumentKind }) {
  const { t } = useTranslation('organisation');
  return (
    <span
      title={t(`emailSending.kindHints.${kind}`)}
      className={`rounded-full px-2 py-px text-[10px] font-semibold ring-1 ${KIND_STYLE[kind].on}`}
    >
      {t(`emailSending.kinds.${kind}`)}
    </span>
  );
}

function StatusPill({ status }: { status: EmailConnection['status'] }) {
  const { t } = useTranslation('organisation');
  const tone =
    status === 'Active'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : status === 'Pending'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-red-200 bg-red-50 text-red-700';
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${tone}`}>
      {t(`emailSending.status.${status}`)}
    </span>
  );
}

/**
 * The provider's own mark, on a white tile. Used only to say which account a
 * mailbox signs in with — the use both brand guidelines allow (as on their
 * "Sign in with…" buttons). Keep the colours and shapes as published.
 */
function ProviderBadge({ provider }: { provider: EmailProvider }) {
  return (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white"
      title={provider === 'Microsoft' ? 'Microsoft 365' : 'Google'}
    >
      {provider === 'Microsoft' ? <MicrosoftLogo /> : <GoogleLogo />}
    </span>
  );
}

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 21 21" className="h-5 w-5" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
